// src/components/ParticipantCard.js
import React from 'react';
import { Card, CardContent, Typography, Box, Chip, Button, Grid, Divider } from '@mui/material'; // Added Grid and Divider
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';

const ParticipantCard = ({ participant, onAction, pipeline }) => {
  const isDue = participant.DueAmount > 0;

  const getPipelineStatus = () => {
    switch (pipeline) {
      case 'finance':
        if (participant.IsFinanciallyCleared) {
          return { message: 'Payment has already been cleared.', color: 'success', icon: <CheckCircleIcon />, actionText: 'View Details', actionDisabled: false };
        }
        if (isDue) {
          return { message: 'Action Required: Outstanding balance needs to be paid.', color: 'error', icon: <ErrorOutlineIcon />, actionText: 'Manage Payment', actionDisabled: false };
        }
        return { message: 'Ready for financial clearance.', color: 'info', icon: <HelpOutlineIcon />, actionText: 'Clear Finance', actionDisabled: false };
      case 'onboarding':
        if (!participant.IsFinanciallyCleared) {
          return { message: 'FINANCE CLEARANCE PENDING. Please see the Finance team.', color: 'error', icon: <ErrorOutlineIcon />, actionText: 'Onboard', actionDisabled: true };
        }
        if (participant.IsOnboarded) {
          return { message: 'Participant has already been onboarded.', color: 'success', icon: <CheckCircleIcon />, actionText: 'View Details', actionDisabled: false };
        }
        return { message: 'Ready for onboarding.', color: 'info', icon: <HelpOutlineIcon />, actionText: 'Onboard', actionDisabled: false };
      case 'attendance':
        if (!participant.IsFinanciallyCleared) {
          return { message: 'FINANCE CLEARANCE PENDING. Cannot record attendance.', color: 'error', icon: <ErrorOutlineIcon />, actionText: 'Check-In', actionDisabled: true };
        }
        return { message: 'Ready for attendance check-in.', color: 'info', icon: <HelpOutlineIcon />, actionText: 'Check-In', actionDisabled: false };
      default:
        return { message: '', color: 'default', actionText: 'Manage', actionDisabled: true };
    }
  };

  const { message, color, icon, actionText, actionDisabled } = getPipelineStatus();

  // Helper to display a value or a placeholder
  const displayInfo = (label, value) => (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{label}</Typography>
      <Typography variant="body2" sx={{ fontWeight: 500 }}>{value || 'N/A'}</Typography>
    </Box>
  );

  return (
    <Card sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'stretch', mb: 2, borderLeft: 5, borderColor: isDue && !participant.IsFinanciallyCleared ? 'error.main' : `${color}.main` }}>
      <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
        {/* Top Section: Name and Due Amount */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
          <Box>
            <Typography variant="h6">{participant.FullName}</Typography>
            <Typography variant="body2" color="text.secondary">{participant.Organization}</Typography>
          </Box>
          {isDue && !participant.IsFinanciallyCleared && (
            <Chip icon={<AttachMoneyIcon />} label={`DUE: $${parseFloat(participant.DueAmount).toFixed(2)}`} color="error" />
          )}
        </Box>

        {/* Middle Section: Detailed Info */}
        <Grid container spacing={2} sx={{ my: 1 }}>
          <Grid item xs={6} sm={3}>{displayInfo("Country", participant.CountryName)}</Grid>
          <Grid item xs={6} sm={3}>{displayInfo("Gender", participant.Gender)}</Grid>
          <Grid item xs={6} sm={3}>{displayInfo("Delegate Type", participant.DelegateType)}</Grid>
          <Grid item xs={6} sm={3}>{displayInfo("Passport No.", participant.PassportNumber)}</Grid>
        </Grid>

        <Divider sx={{ my: 1.5 }} />
        
        {/* Bottom Section: Status Message */}
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {icon}
            <Typography variant="subtitle2" color={`${color}.dark`} sx={{ ml: 1, fontWeight: 'bold' }}>
              {message}
            </Typography>
        </Box>
      </CardContent>

      {/* Action Button on the right */}
      <Box sx={{ p: 2, flexShrink: 0, display: 'flex', alignItems: 'center', borderLeft: '1px solid rgba(0, 0, 0, 0.12)' }}>
        <Button
          variant="contained"
          onClick={() => onAction(participant.ParticipantID)}
          disabled={actionDisabled}
          sx={{ minWidth: 140 }}
        >
          {actionText}
        </Button>
      </Box>
    </Card>
  );
};

export default ParticipantCard;